import { useState } from 'react';
import { useTranslation } from '../../../lib/i18n';
import { updateRoutine, type WebRoutine } from '../../../lib/routines';
import { canSaveRoutineEdit, routineEdit, type RoutineEditDraft } from '../../../lib/routine-edit';
import { Input } from '../ui';
import styles from './routines.module.css';

/**
 * Where a save failure belongs on the form. A rejected CRON is attached to the SCHEDULE field rather than
 * shown as a form-level line, because "couldn't save" does not tell you which of four fields to fix — and
 * a routine with a cron the server won't take simply stops firing. Mirrors iOS `RoutineEditError`.
 */
export type RoutineEditError = { field: 'schedule' | 'form'; message: string };

/**
 * The routine's editable fields — name, description and cron — written through the
 * `update-routine` action both phones use (see `lib/routines.ts`).
 *
 * **Only what changed is sent**, and an untouched Save writes nothing at all: the rule is the pure
 * `routineEdit` diff in `lib/routine-edit.ts`, shared with both natives and unit-tested there.
 *
 * The cron is offered as the RAW expression rather than a friendly picker: it is what the entity stores
 * and what the assistant writes, a picker would have to round-trip through a grammar it cannot express
 * (`MON-FRI`, day-of-month lists), and silently rewriting a routine's schedule into the nearest thing a
 * picker can say is worse than showing the author the string they authored. The card's schedule line
 * already renders it in words.
 */
export function useRoutineEdit(routine: WebRoutine, onSaved: () => void) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<RoutineEditDraft>({
    name: routine.name ?? '',
    description: routine.description ?? '',
    cron: routine.cron ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<RoutineEditError | null>(null);

  const patch = (part: Partial<RoutineEditDraft>) => setDraft((current) => ({ ...current, ...part }));
  const canSave = canSaveRoutineEdit(draft);

  const save = async (): Promise<boolean> => {
    if (!canSave) return false;
    const edit = routineEdit(routine, draft);
    // Nothing changed — close without a request. Restating unchanged values would clobber a concurrent
    // edit from the agent's own tools, and the server answers `400 no_updates` to an empty write.
    if (!edit) {
      onSaved();
      return true;
    }
    setSaving(true);
    setError(null);
    const result = await updateRoutine(routine, edit);
    setSaving(false);
    if (!result.ok) {
      setError(
        result.reason === 'invalid_cron'
          ? { field: 'schedule', message: t('routines.edit.error.invalidCron') }
          : { field: 'form', message: result.message },
      );
      return false;
    }
    onSaved();
    return true;
  };

  return {
    fields: {
      name: draft.name,
      setName: (name: string) => patch({ name }),
      description: draft.description,
      setDescription: (description: string) => patch({ description }),
      cron: draft.cron,
      setCron: (cron: string) => patch({ cron }),
    },
    saving,
    canSave,
    error,
    save,
  };
}

export function RoutineEditForm({
  fields,
  error,
}: {
  fields: ReturnType<typeof useRoutineEdit>['fields'];
  error: RoutineEditError | null;
}) {
  const { t } = useTranslation();
  const scheduleError = error?.field === 'schedule' ? error.message : null;
  return (
    <div className={styles.form}>
      <Input
        label={t('routines.edit.name')}
        value={fields.name}
        onChange={(event) => fields.setName(event.target.value)}
      />
      <Input
        label={t('routines.edit.description')}
        value={fields.description}
        onChange={(event) => fields.setDescription(event.target.value)}
      />
      <Input
        label={t('routines.edit.cron')}
        status={scheduleError ? 'error' : 'default'}
        statusMessage={scheduleError ?? t('routines.edit.cronHint')}
        value={fields.cron}
        onChange={(event) => fields.setCron(event.target.value)}
      />
      {error?.field === 'form' && <span className={styles.error}>{error.message}</span>}
    </div>
  );
}
